import { defineAsyncComponent, shallowRef, type Ref } from 'vue';
import type { Step } from '@/widgets/Wizard/models/step';
import type { MarkupElement } from '@/widgets/Wizard/models/markup';
import { useMetadataStore } from '../stores/metadata';
import { storeToRefs } from 'pinia';
import { useReservationStore } from '../stores/reservation';
import { useI18n } from 'vue-i18n';
import { usePaymentStore } from '../stores/payment';

/**
 * This step will add a language select screen to the booking wizard flow.
 */
export class LanguageSelectStep implements Step {
    public code = 'language-select';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/language-select.vue')),
    );

    public supportedLanguages: string[] = [];
    public description: string | null = null;

    private metaDataStore: ReturnType<typeof useMetadataStore> | null = null;
    private locale: Ref<string> | null = null;

    public constructor(supportedLanguages: string[], description: string | null = null) {
        this.supportedLanguages = supportedLanguages;
        this.description = description;
    }

    public props() {
        return {
            supportedLanguages: this.supportedLanguages,
            description: this.description,
        };
    }

    public setup() {
        this.metaDataStore = useMetadataStore();
        this.locale = useI18n().locale;
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        if (!this.metaDataStore || !this.locale) {
            return;
        }

        this.metaDataStore.updateCountries(this.locale.value);
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

/**
 * This step will add a page that displays HTML code to the booking wizard flow.
 */
export class HtmlPageStep implements Step {
    public code = 'html-page';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/html-page.vue')),
    );

    private content: MarkupElement[];

    public constructor(content: MarkupElement[] = []) {
        this.content = content;
    }

    public props() {
        return {
            content: this.content,
        };
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class FlowSelectStep implements Step {
    public code = 'flow-select';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/flow-select.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class TravelGroupStep implements Step {
    public code = 'travel-group';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/travel-group.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class StayDurationStep implements Step {
    public code = 'stay-duration';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/stay-duration.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class PickAccommodationStep implements Step {
    public code = 'pick-accommodation';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/pick-accommodation.vue')),
    );

    private reservationStore: ReturnType<typeof useReservationStore> | null = null;

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
        this.reservationStore = useReservationStore();
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        if (!this.reservationStore) {
            return false;
        }

        const { hasValidStayPeriod } = storeToRefs(this.reservationStore);

        return hasValidStayPeriod.value;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class PetsStep implements Step {
    public code = 'pets';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/pets.vue')),
    );

    private metaDataStore: ReturnType<typeof useMetadataStore> | null = null;

    public props() {
        return {};
    }

    public setup() {
        this.metaDataStore = useMetadataStore();
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        if (!this.metaDataStore) {
            return true;
        }

        const { activeAccount } = storeToRefs(this.metaDataStore);

        if (!activeAccount.value || !activeAccount.value.options.pets || !activeAccount.value.options.pets.property) {
            return true;
        }

        return false;
    }
}

export class PickAccommodationObjectStep implements Step {
    public code = 'pick-accommodation-object';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/pick-accommodation-object.vue')),
    );

    private skip: boolean = false;
    private reservationStore: ReturnType<typeof useReservationStore> | null = null;

    public props() {
        return {};
    }

    public setup() {
        const metaDataStore = useMetadataStore();
        const { getWizardOptions } = storeToRefs(metaDataStore);

        this.reservationStore = useReservationStore();

        if (getWizardOptions.value === null) {
            return;
        }

        this.skip = !getWizardOptions.value.useMap;
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        if (this.skip || prev) {
            return true;
        }

        if (!this.reservationStore) {
            return false;
        }

        return this.reservationStore.chosenAccommodationLocation !== null;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return this.skip;
    }
}

export class PersonalDetailsStep implements Step {
    public code = 'personal-details';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/personal-details.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class ReceiptConfirmStep implements Step {
    public code = 'receipt-confirm';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/receipt-confirm.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class ExtrasStep implements Step {
    public code = 'extras';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/extras.vue')),
    );

    private metaDataStore: ReturnType<typeof useMetadataStore> | null = null;
    private reservationStore: ReturnType<typeof useReservationStore> | null = null;

    public props() {
        return {};
    }

    public setup() {
        this.metaDataStore = useMetadataStore();
        this.reservationStore = useReservationStore();
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        if (!this.metaDataStore || !this.reservationStore) {
            return true;
        }

        const { getAccommodationExtras } = storeToRefs(this.metaDataStore);
        const { chosenAccommodation } = storeToRefs(this.reservationStore);

        if (!chosenAccommodation.value) {
            return true;
        }

        return getAccommodationExtras.value(chosenAccommodation.value.id).length === 0;
    }
}

export class ReceiptStep implements Step {
    public code = 'receipt';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/receipt.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class PinPaymentStep implements Step {
    public code = 'pin-payment';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/pin-payment.vue')),
    );

    private metaDataStore: ReturnType<typeof useMetadataStore> | null = null;
    private reservationStore: ReturnType<typeof useReservationStore> | null = null;

    public props() {
        return {};
    }

    public setup() {
        this.metaDataStore = useMetadataStore();
        this.reservationStore = useReservationStore();
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        if (!this.metaDataStore || !this.reservationStore) {
            return true;
        }

        const { activeAccount } = storeToRefs(this.metaDataStore);
        const { booking, bookingFailed } = storeToRefs(this.reservationStore);

        if (!activeAccount.value || !activeAccount.value.options.usePin) {
            return true;
        }

        if (!booking.value || bookingFailed.value) {
            return true;
        }

        return false;
    }
}

export class IdealCheckoutStep implements Step {
    public code = 'ideal-checkout';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/ideal-checkout.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class ThanksStep implements Step {
    public code = 'thanks';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/book/thanks.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class CheckInReservationNumberStep implements Step {
    public code = 'check-in-reservation-number';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/check-in/reservation-number.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class CheckInPinPaymentStep implements Step {
    public code = 'check-in-pin-payment';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/check-in/pin-payment.vue')),
    );

    private metaDataStore: ReturnType<typeof useMetadataStore> | null = null;
    private paymentStore: ReturnType<typeof usePaymentStore> | null = null;

    public props() {
        return {};
    }

    public setup() {
        this.metaDataStore = useMetadataStore();
        this.paymentStore = usePaymentStore();
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        if (!this.metaDataStore) {
            return true;
        }

        const { activeAccount } = storeToRefs(this.metaDataStore);

        if (!activeAccount.value || !activeAccount.value.options.usePin) {
            return true;
        }

        if (this.paymentStore) {
            const { bookingHasAlreadyBeenPaid } = storeToRefs(this.paymentStore);

            if (bookingHasAlreadyBeenPaid.value) {
                return true;
            }
        }

        return false;
    }
}

export class CheckInThanksStep implements Step {
    public code = 'check-in-thanks';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/check-in/thanks.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}

export class SkipStep implements Step {
    public code = 'skip-step';
    public component = shallowRef(
        defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/flow-select.vue')),
    );

    public props() {
        return {};
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return false;
    }

    public shouldBeSkipped() {
        return true;
    }
}

export class RedirectStep implements Step {
    public code = 'redirect-step';
    public component = shallowRef(defineAsyncComponent(() => import('@/widgets/Wizard/components/steps/redirect.vue')));

    private redirects: { [translation: string]: string };

    public constructor(redirects: { [translation: string]: string }) {
        this.redirects = redirects;
    }

    public props() {
        return {
            redirects: this.redirects,
        };
    }

    public setup() {
        // TODO: Implement...
    }

    public activate() {
        // TODO: Implement...
    }

    public deactivate() {
        // TODO: Implement...
    }

    public canBeDeactivated(prev: boolean, next: boolean) {
        return true;
    }

    public canBeActivated() {
        return true;
    }

    public shouldBeSkipped() {
        return false;
    }
}
